import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronRight, ArrowUpRight, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";

import { OFundedLogo } from "@/components/BrandLogo";
import { PLATFORM_MAP } from "@/components/Navbar.MegaMenu";
import { prefetchProps } from "@/lib/prefetch";

/* Mobile full-screen overlay menu · split out of Navbar.jsx (P2 component
   breakdown). Renders the entire site IA as a single scrollable surface
   so visitors can scan everything in two thumb-scrolls.

   Props:
     • open                  · whether the overlay is visible
     • onClose               · close handler (parent owns the state)
     • menuEntries           · translated mega-menu entry list
     • signinUrl             · external CRM sign-in link
     • openAccountHref       · CTA target (CRM URL or signup-modal sentinel)
     • openAccountIsExternal · whether the CTA should open in a new tab */
export default function NavbarMobileMenu({
  open,
  onClose,
  menuEntries,
  signinUrl,
  openAccountHref,
  openAccountIsExternal,
}) {
  const { t } = useTranslation();

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="mobile-fs"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="lg:hidden fixed top-0 left-0 right-0 bottom-0 w-screen h-screen z-[70] flex flex-col"
          data-testid="mobile-menu"
          style={{ background: "var(--surface)", height: "100dvh", width: "100vw" }}
        >
          {/* Header bar · logo + close */}
          <div className="flex items-center justify-between px-5 h-16 border-b border-[color:var(--line)]/60 shrink-0">
            <Link to="/" onClick={onClose} className="flex items-center">
              <OFundedLogo className="h-7 w-auto" />
            </Link>
            <button
              type="button"
              onClick={onClose}
              aria-label={t("nav.closeMenu", "Close menu")}
              data-testid="mobile-menu-close"
              className="w-10 h-10 rounded-full grid place-items-center text-[color:var(--text-1)] hover:bg-[color:var(--surface-2)] transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Body · scrollable icon-tile grid */}
          <div
            className="flex-1 overflow-y-auto px-3 pt-3 pb-32"
            data-testid="mobile-menu-scroll"
            style={{ overscrollBehavior: "contain" }}
          >
            {menuEntries.map((entry) => (
              <MobileCategorySection key={entry.key} entry={entry} onClose={onClose} />
            ))}

            {/* Prop Trading · sister-brand spotlight */}
            <section className="mt-5" data-testid="mobile-cat-prop-trading">
              <a
                href="https://ofunded.com"
                target="_blank"
                rel="noreferrer"
                onClick={onClose}
                className="flex items-center gap-3 px-4 py-4 rounded-xl"
                style={{
                  background: "linear-gradient(135deg, color-mix(in srgb, var(--brand) 14%, var(--surface-2)) 0%, var(--surface-2) 75%)",
                  border: "1px solid color-mix(in srgb, var(--brand) 30%, transparent)",
                }}
                data-testid="mobile-link-prop-trading"
              >
                <span
                  className="shrink-0 w-10 h-10 rounded-lg grid place-items-center"
                  style={{ background: "color-mix(in srgb, var(--brand) 18%, transparent)" }}
                >
                  <Sparkles size={18} className="text-brand" strokeWidth={1.8} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[12px] uppercase tracking-[0.22em] text-brand font-semibold">
                    {t("nav.sisterBrand", "Sister brand")}
                  </span>
                  <span className="block text-[14px] font-medium text-[color:var(--text-1)] leading-tight mt-0.5">
                    {t("nav.propTrading", "Prop Trading")}
                  </span>
                  <span className="block text-[12px] text-[color:var(--text-3)] truncate font-light leading-tight mt-0.5">
                    {t("nav.sisterBrandBlurb", "Trade funded accounts on ofunded.com")}
                  </span>
                </span>
                <ArrowUpRight size={16} className="text-brand shrink-0" />
              </a>
            </section>
          </div>

          {/* Sticky CTA pair · always reachable */}
          <div
            className="p-3 border-t border-[color:var(--line)]/60 flex items-center gap-3 shrink-0"
            style={{
              paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))",
              background: "var(--surface)",
            }}
          >
            <a
              href={signinUrl}
              target="_blank"
              rel="noreferrer"
              onClick={onClose}
              data-testid="mobile-cta-signin"
              className="flex-1 h-12 inline-flex items-center justify-center text-[14px] font-medium rounded-xl border border-[color:var(--line-strong)] text-[color:var(--text-1)]"
            >
              {t("nav.signIn")}
            </a>
            <a
              href={openAccountHref}
              target={openAccountIsExternal ? "_blank" : undefined}
              rel={openAccountIsExternal ? "noreferrer" : undefined}
              onClick={onClose}
              data-testid="mobile-cta-register"
              className="flex-1 h-12 inline-flex items-center justify-center text-[14px] font-semibold rounded-xl"
              style={{
                background: "linear-gradient(135deg, var(--brand) 0%, var(--brand-deep) 100%)",
                color: "var(--brand-ink)",
                boxShadow: "var(--shadow-brand-cta)",
              }}
            >
              {t("nav.register")}
            </a>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* Per-category section · title + list of links + optional spotlight CTA.
   Internal helper · not exported. */
function MobileCategorySection({ entry, onClose }) {
  return (
    <section
      id={`m-cat-${entry.key}`}
      data-testid={`mobile-cat-${entry.key}`}
      className="mt-5 first:mt-3"
    >
      <div className="px-1 mb-2.5">
        <h3 className="text-[15px] font-semibold text-[color:var(--text-1)] tracking-tight">
          {entry.label}
        </h3>
      </div>

      {entry.columns.map((col) => (
        <div key={col.heading} className="mb-3 last:mb-0">
          <div className="text-[12px] uppercase tracking-[0.22em] text-[color:var(--text-3)] font-semibold mb-2 px-1">
            {col.heading}
          </div>
          <div
            className="rounded-xl border"
            style={{
              borderColor: "var(--line)",
              background: "color-mix(in srgb, var(--surface-2) 60%, transparent)",
            }}
          >
            {col.items.map((it, idx) => (
              <MobileLink key={it.label} item={it} firstRow={idx === 0} onClose={onClose} />
            ))}
          </div>
        </div>
      ))}

      {entry.spotlight && (
        <Link
          to={entry.spotlight.cta.to}
          onClick={onClose}
          data-testid={`mobile-spotlight-${entry.key}`}
          className="mt-3 flex items-center gap-3 px-4 py-3 rounded-xl"
          style={{
            background:
              "linear-gradient(135deg, color-mix(in srgb, var(--brand) 18%, var(--surface-2)) 0%, var(--surface-2) 70%)",
            border: "1px solid color-mix(in srgb, var(--brand) 32%, transparent)",
          }}
        >
          <span className="shrink-0 inline-flex w-6 h-6 items-center justify-center">
            {entry.spotlight.icon ? (
              <entry.spotlight.icon size={18} className="text-brand" strokeWidth={1.8} />
            ) : (
              <Sparkles size={18} className="text-brand" strokeWidth={1.8} />
            )}
          </span>
          <span className="min-w-0 flex-1 text-left">
            <span className="block text-[12px] uppercase tracking-[0.22em] text-brand font-semibold">
              {entry.spotlight.eyebrow}
            </span>
            <span className="block text-[13px] font-medium text-[color:var(--text-1)] leading-tight mt-0.5 truncate">
              {entry.spotlight.title}
            </span>
          </span>
          <ArrowUpRight size={16} className="text-brand shrink-0" />
        </Link>
      )}
    </section>
  );
}

function MobileLink({ item, firstRow, onClose }) {
  const plat = item.platformId ? PLATFORM_MAP[item.platformId] : null;
  return (
    <Link
      to={item.to}
      onClick={onClose}
      data-testid={`mobile-link-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      {...prefetchProps(item.to.split("?")[0])}
      className={`flex items-center gap-3 px-3 py-3 active:bg-[color:var(--surface-2)] transition-colors ${
        firstRow ? "" : "border-t border-[color:var(--line)]"
      }`}
    >
      <span className="shrink-0 inline-flex w-6 h-6 items-center justify-center">
        {plat ? (
          <img
            src={plat.logo}
            alt={plat.name}
            className="w-5 h-5 object-contain"
            style={{ filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.3))" }}
            loading="lazy"
          />
        ) : (
          <item.Icon size={18} strokeWidth={1.7} className="text-brand" />
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13.5px] font-medium text-[color:var(--text-1)] leading-tight">
          {item.label}
        </span>
        {item.tagline && (
          <span className="block text-[12px] text-[color:var(--text-3)] truncate font-light leading-tight mt-0.5">
            {item.tagline}
          </span>
        )}
      </span>
      <ChevronRight size={14} className="text-[color:var(--text-3)] shrink-0" />
    </Link>
  );
}
